import { useState } from "react";
import { Loader2, CheckCircle2, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createLostProperty } from "@/lib/api";

const inputCls = "bg-surface border-transparent rounded-xl h-12 px-4 text-ink placeholder:text-ink/35 focus:bg-white focus:border-brand focus-visible:ring-2 focus-visible:ring-brand/20 transition-colors";
const labelCls = "text-[11px] font-bold text-ink/50 uppercase tracking-[0.12em] mb-2 block";

const empty = {
  name: "", email: "", phone: "",
  journey_date: "", pickup: "", dropoff: "",
  vehicle_desc: "", item_description: "", additional_info: "",
};

export default function LostProperty() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.journey_date || !form.item_description) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setLoading(true);
    try {
      await createLostProperty(form);
      setDone(true);
      toast.success("Lost property report submitted. We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please call 01293 515151.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Seo title="Report Lost Property" description="Left something in a Clocktower Cars vehicle? Report it here and we will contact you within 24 hours." path="/lost-property" />

      <section className="bg-ink py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-brand-light">Lost property</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black text-white tracking-tight leading-[1.05]">
            Left something in the car?
          </h1>
          <p className="mt-5 text-white/60 max-w-xl leading-relaxed">
            Give us as much detail as you can and we&apos;ll ask the driver to check the vehicle straight away. We aim to reply within 24 hours.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {done ? (
            <div data-testid="lost-success" className="bg-surface border border-slate-200 rounded-2xl p-10 text-center">
              <div className="grid place-items-center h-14 w-14 rounded-full bg-brand/10 text-brand mx-auto mb-5">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-ink mb-2">Report received</h2>
              <p className="text-ink/60 max-w-md mx-auto">We&apos;ll ask the driver to check the vehicle and reply to {form.email} within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="lost-form" className="grid gap-6">
              <div className="bg-surface border border-slate-200 rounded-xl p-4 flex gap-3 items-start">
                <PackageSearch className="h-5 w-5 mt-0.5 shrink-0 text-brand" />
                <p className="text-sm text-ink/70 leading-relaxed">
                  Items are held securely at our Crawley office for 3 months. Collection is free — or we can post tracked at actual cost.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className={labelCls}>Your name *</Label>
                  <Input data-testid="lost-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} required />
                </div>
                <div>
                  <Label className={labelCls}>Email *</Label>
                  <Input data-testid="lost-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} required />
                </div>
                <div>
                  <Label className={labelCls}>Phone *</Label>
                  <Input data-testid="lost-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
                </div>
                <div>
                  <Label className={labelCls}>Journey date *</Label>
                  <Input data-testid="lost-date" type="date" className={inputCls} value={form.journey_date} onChange={(e) => set("journey_date", e.target.value)} required />
                </div>
                <div>
                  <Label className={labelCls}>Pickup location</Label>
                  <Input data-testid="lost-pickup" className={inputCls} value={form.pickup} onChange={(e) => set("pickup", e.target.value)} placeholder="e.g. Gatwick Airport South Terminal" />
                </div>
                <div>
                  <Label className={labelCls}>Drop-off location</Label>
                  <Input data-testid="lost-dropoff" className={inputCls} value={form.dropoff} onChange={(e) => set("dropoff", e.target.value)} placeholder="e.g. Crawley RH10" />
                </div>
                <div className="sm:col-span-2">
                  <Label className={labelCls}>Vehicle description</Label>
                  <Input data-testid="lost-vehicle" className={inputCls} value={form.vehicle_desc} onChange={(e) => set("vehicle_desc", e.target.value)} placeholder="e.g. black Mercedes saloon, driver's name if known" />
                </div>
              </div>

              <div>
                <Label className={labelCls}>Description of item *</Label>
                <Textarea
                  data-testid="lost-item"
                  className={`${inputCls} h-32 py-3`}
                  value={form.item_description}
                  onChange={(e) => set("item_description", e.target.value)}
                  placeholder="Be as specific as possible — colour, brand, model, distinguishing features."
                  required
                />
              </div>

              <div>
                <Label className={labelCls}>Anything else that would help</Label>
                <Textarea
                  data-testid="lost-notes"
                  className={`${inputCls} h-24 py-3`}
                  value={form.additional_info}
                  onChange={(e) => set("additional_info", e.target.value)}
                  placeholder="Where in the vehicle it might be, the time of the journey, etc."
                />
              </div>

              <Button data-testid="lost-submit" type="submit" disabled={loading} className="bg-brand hover:bg-brand-light text-white h-14 rounded-full font-bold text-base">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Submitting…</> : "Submit report"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
}
